import type { QuadPoints, Vec2 } from './homography';

/** Fallback when the mask gives no usable plane: a centered trapezoid on the lower half. */
export const DEFAULT_QUAD: QuadPoints = [
  [0.3, 0.45],
  [0.7, 0.45],
  [1, 1],
  [0, 1],
];

function cross(o: Vec2, a: Vec2, b: Vec2) {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

export function isConvex(q: QuadPoints): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const c = cross(q[i], q[(i + 1) % 4], q[(i + 2) % 4]);
    if (Math.abs(c) < 1e-9) return false;
    const s = Math.sign(c);
    if (sign && s !== sign) return false;
    sign = s;
  }
  return true;
}

/** Line x = a + b * y. */
interface Line {
  a: number;
  b: number;
}

function leastSquares(ys: number[], xs: number[]): Line | null {
  const n = ys.length;
  let sy = 0;
  let sx = 0;
  let syy = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    sy += ys[i];
    sx += xs[i];
    syy += ys[i] * ys[i];
    sxy += ys[i] * xs[i];
  }
  const den = n * syy - sy * sy;
  if (n < 2 || Math.abs(den) < 1e-9) return null;
  const b = (n * sxy - sy * sx) / den;
  return { a: (sx - b * sy) / n, b };
}

/**
 * Robust line through a floor side (where it meets a wall): least squares,
 * then twice refit on the best 60% so cars, shelves and doorways that bite
 * into the mask do not pull the line.
 */
function fitSide(ys: number[], xs: number[]): Line | null {
  let line = leastSquares(ys, xs);
  for (let pass = 0; pass < 2 && line; pass++) {
    const l: Line = line;
    const res = ys.map((y, i) => Math.abs(xs[i] - (l.a + l.b * y)));
    const cut = [...res].sort((p, q) => p - q)[Math.floor(res.length * 0.6)];
    const keep = res.map((r, i) => (r <= cut ? i : -1)).filter((i) => i >= 0);
    line = leastSquares(keep.map((i) => ys[i]), keep.map((i) => xs[i]));
  }
  return line;
}

/**
 * Estimates the floor plane as a quad from a binary mask (value > 127 = floor).
 * Returns corners normalized to 0..1 (top-left, top-right, bottom-right, bottom-left).
 *
 * The far edge is the top of the floor. The sides follow the floor's left and
 * right edges as straight lines, extended down to the bottom of the photo even
 * where that lies outside the frame: the lines converge like the real floor
 * edges do, which is what makes near chips bigger than far ones. Sides the
 * photo does not show fall back to the mask's extent (or a mirror of the
 * other side).
 */
export function autoQuad(mask: Uint8Array | Uint8ClampedArray, width: number, height: number): QuadPoints {
  const left = new Int32Array(height).fill(-1);
  const right = new Int32Array(height).fill(-1);
  const rows: number[] = [];
  for (let y = 0; y < height; y++) {
    const o = y * width;
    let l = -1;
    let r = -1;
    for (let x = 0; x < width; x++) {
      if (mask[o + x] > 127) {
        if (l < 0) l = x;
        r = x;
      }
    }
    if (l >= 0) {
      left[y] = l;
      right[y] = r;
      rows.push(y);
    }
  }
  if (rows.length < 2) return DEFAULT_QUAD;

  // Average the extents over a thin band of floor rows around a percentile.
  const band = Math.max(1, Math.round(rows.length * 0.02));
  const extents = (p: number) => {
    const k = Math.round(p * (rows.length - 1));
    let l = 0;
    let r = 0;
    let y = 0;
    let n = 0;
    for (let j = Math.max(0, k - band); j <= Math.min(rows.length - 1, k + band); j++) {
      l += left[rows[j]];
      r += right[rows[j]];
      y += rows[j];
      n++;
    }
    return { l: l / n, r: r / n, y: y / n };
  };
  const top = extents(0.1);
  const bottom = extents(0.95);
  const bottomY = rows[rows.length - 1] >= height - 1 ? height : bottom.y + 1;

  // Side samples: floor rows below the far edge where the side is inside the photo.
  const edge = Math.max(1, width * 0.005);
  const ly: number[] = [];
  const lx: number[] = [];
  const ry: number[] = [];
  const rx: number[] = [];
  for (const y of rows) {
    if (y < top.y) continue;
    if (left[y] > edge) ly.push(y), lx.push(left[y]);
    if (right[y] < width - 1 - edge) ry.push(y), rx.push(right[y] + 1);
  }
  // A side counts when it is seen over a good part of the floor's height.
  const span = bottomY - top.y;
  const usable = (ys: number[]) => ys.length >= Math.max(8, span * 0.25) && ys[ys.length - 1] - ys[0] >= span * 0.25;
  let lLine = usable(ly) ? fitSide(ly, lx) : null;
  let rLine = usable(ry) ? fitSide(ry, rx) : null;
  // Floor edges spread apart toward the camera; anything else is not a side.
  if (lLine && lLine.b >= -0.02) lLine = null;
  if (rLine && rLine.b <= 0.02) rLine = null;

  let quad: QuadPoints | null = null;
  if (lLine || rLine) {
    const tl = top.l <= edge ? 0 : top.l;
    const tr = top.r >= width - 1 - edge ? width : top.r + 1;
    // Unseen side: mirror the seen one around the floor's center.
    const L = lLine ?? { a: tl + rLine!.b * top.y, b: -rLine!.b };
    const R = rLine ?? { a: tr + lLine!.b * top.y, b: -lLine!.b };
    const at = (s: Line, y: number) => s.a + s.b * y;
    // Near edge: as low as possible while both corners stay in the photo, so
    // every handle can be grabbed. Where it sits does not change the look:
    // the side lines set the perspective and the renderer measures depth.
    const y0 = top.y;
    const y1 = Math.min(bottomY, (0 - L.a) / L.b, (width - R.a) / R.b);
    if (y1 - y0 >= height * 0.06) {
      const nx = (x: number) => Math.min(1, Math.max(0, x / width));
      quad = [
        [nx(at(L, y0)), y0 / height],
        [nx(at(R, y0)), y0 / height],
        [nx(at(R, y1)), y1 / height],
        [nx(at(L, y1)), y1 / height],
      ];
    }
  }
  if (!quad) {
    // No side visible (the floor fills the frame): use the mask's extent.
    const nx = (x: number, isLeft: boolean) => {
      if (isLeft && x <= edge) return 0;
      if (!isLeft && x >= width - 1 - edge) return 1;
      return (isLeft ? x : x + 1) / width;
    };
    quad = [
      [nx(top.l, true), top.y / height],
      [nx(top.r, false), top.y / height],
      [nx(bottom.r, false), bottomY / height],
      [nx(bottom.l, true), bottomY / height],
    ];
  }

  const topWidth = quad[1][0] - quad[0][0];
  const bottomWidth = quad[2][0] - quad[3][0];
  if (!isConvex(quad) || topWidth <= 0 || bottomWidth < topWidth || quad[2][1] - quad[1][1] < 0.02) return DEFAULT_QUAD;
  return quad;
}
